import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Button, createStyles, Group, Stepper } from "@mantine/core";
import { ConfigureInput } from "./configure-input";
import { ConfigureFormula } from "./configure-formula";
import { ValidateFormula } from "./validate-formula";
import t from "../../stores/language";
import { useParams } from "react-router-dom";
import { useStore } from "../../stores/store";
import { ModuleAssumptions } from "./module-assumptions";

export const useStyles = createStyles((theme, params: { resultErrors?: boolean; calculatedFormulaResult?: number } | null) => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  stepperContainer: {
    boxShadow: theme.other.arc.shadows.lg,
    padding: theme.other.arc.paddings[2],
    flex: 1,
  },
  steps: {
    height: "calc(100vh - 250px)",
    maxHeight: "calc(100vh - 265px)",
    overflow: "hidden",
  },
  title: {
    height: theme.other.formula.title.height,
    boxShadow: theme.other.arc.shadows.sm,
    display: "flex",
    marginTop: theme.other.arc.margins[4],
    color: theme.other?.arc.colors[1],
    fontWeight: "bold",
    alignItems: "center",
    padding: theme.other.arc.paddings[1],
  },
  calculatedResultItems: {
    boxShadow: theme.other.arc.shadows.xs,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "80px",
    padding: theme.other.arc.paddings[2],
  },
  calculationResult: {
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: (params?.calculatedFormulaResult ?? 0) > 2 ? "repeat(3, 1fr)" : "initial",
    padding: theme.other.arc.paddings[1],
    gap: theme.other?.arc.margins[3],
    justifyContent: "center",
    gridAutoFlow: (params?.calculatedFormulaResult ?? 0) > 2 ? "initial" : "column",
  },
  calculationResultContainer: {
    marginTop: theme.other?.arc.margins[2],
    backgroundColor: theme?.other?.arc.colors[3],
    minHeight: params?.resultErrors || params?.calculatedFormulaResult === 0 ? "initial" : "92px",
  },
  iconButton: {
    "&: hover": {
      backgroundColor: "transparent",
    },
  },
}));

const FormulaBuilder = observer(function FormulaBuilder() {
  const { classes } = useStyles(null);
  const [active, setActive] = useState(0);
  const { id } = useParams();
  const { store } = useStore();
  useEffect(() => {
    if (store.inputElements.length === 0 || id == null) return;
    const defaultValues: { [key: string]: string | number | null } = {};
    const inputs = store.GetInputByModule(id);
    inputs.forEach((i) => {
      defaultValues[i.id] = i.default != null && i.default != "" ? i.default : i.type === "Number Input" ? 0 : "";
    });
    store.UpdateSelectedInputs(defaultValues);
  }, [id, store.inputElements.length]);

  useEffect(() => {
    setActive(0);
  }, [id]);

  return (
    <div className={classes.container}>
      {id != null ? (
        <>
          <Stepper className={classes.stepperContainer} active={active} onStepClick={setActive} breakpoint="sm">
            <Stepper.Step label={t("Standard Assumptions")} description={t("Add Standard Assumptions")}>
              <div className={classes.steps}>
                <ModuleAssumptions />
              </div>
            </Stepper.Step>
            <Stepper.Step label={t("Configure Inputs")} description={t("Select required inputs")}>
              <div className={classes.steps}>
                <ConfigureInput />
              </div>
            </Stepper.Step>
            <Stepper.Step label={t("Configure Formulas")} description={t("Setup formulas")}>
              <div className={classes.steps}>
                <ConfigureFormula />
              </div>
            </Stepper.Step>
            <Stepper.Step label={t("Validate Formulas")} description={t("Validate formulas")}>
              <div className={classes.steps}>
                <ValidateFormula />
              </div>
            </Stepper.Step>
            <Stepper.Completed>{t("Completed, click back button to get to previous step")}</Stepper.Completed>
          </Stepper>
          <Group position="center" mt={10}>
            {active > 0 ? (
              <Button variant="default" onClick={() => setActive(active != 0 ? active - 1 : active)}>
                Back
              </Button>
            ) : null}
            <Button onClick={() => setActive(active != 4 ? active + 1 : active)}>Next step</Button>
          </Group>
        </>
      ) : (
        <div className={classes.title}>{t("Please add and select one module to create formula")}</div>
      )}
    </div>
  );
});
export default FormulaBuilder;
